import { useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Search, X, Sparkles, Stethoscope } from 'lucide-react'
import useQuestionnaireStore from '../stores/useQuestionnaireStore'
import { useI18n } from '../i18n'
import { useQuestionnaireTranslation } from '../i18n/questionnaires/useQuestionnaireTranslation'
import { getSpecialtyIcon } from '../utils/specialtyIcons'
import { browsableSpecialties, isPatientSelectable } from '../utils/specialtyResolver'
import { aiAPI } from '../services/api'
import BottomSheet from '../components/BottomSheet'
import NotificationBellButton from '../components/NotificationBellButton'
import { OrellaDialogue } from './onboarding/onboardingPrimitives'
import { useCaseStore } from '../stores'
import { caseDoctor } from '../utils/caseDoctor'
import { doctorMatchesCategory } from '../config/specialties'
import { doctorAreas, withGeneral } from '../utils/mdCaseArea'
import { clinicianName } from '../utils/clinicianName.js'

/**
 * SpecialtyBrowseScreen — v242.158.0
 *
 * ★★★ THE 23 AREAS LEFT HOME, AND THE MEASUREMENT IS WHY. The 2-up grid with a
 *     per-cell "Learn more" footer rendered 1,322px tall on a 402x874 device —
 *     1.51 screens for one block, sitting between "Start a new case" and
 *     "Continue prior case". Deleting the 23 duplicate footers in place would
 *     have returned 423px (-32%); moving the block here returns 1,180px (-89%).
 *     Measured side by side at 402x874, not estimated: orella_specialty_density_v1.html.
 *
 * ★★ AND GOING 3-UP ON HOME WOULD HAVE BOUGHT NOTHING. At three columns the
 *    long labels ("Veins & Circulation", "Wellness Checks") wrap to two lines,
 *    and the cell height is set by the LABEL, not the icon — so the 3-up variant
 *    measured 20px TALLER than the same 2-up grid with its footer removed. The
 *    entire in-place win was the footer. Density was never the lever; location was.
 *
 * ★ SEARCH MATCHES FOCUS AREAS, NOT JUST TITLES. A patient with a rash does not
 *   look for "Skin" — the word they have is the symptom. Every specialty carries
 *   its focusAreas list already, so matching against it costs nothing and is the
 *   difference between a search box and a filter box.
 *
 * ★ s99 (Dr. Rami 2026-10-10, option A of "Orella Flow Edits"): one ROW per area — icon, name,
 *   and a small sparkle button for Learn more (the 23 "Learn more" text links are gone). A row is
 *   ~61px against ~142px for a row of three tiles. On a wide screen (iPad) the rows go two
 *   columns. No focus-area line under the name (his call: names only).
 */

const NAVY = '#15354F'
const MUTE = '#5F7A8E'
const BLUE = '#46A8F9'
const LINE = '#E4EBF2'
const ICE = '#E9F8FF'

// ★ v242.234.0 (Build 59) — GENERAL CONCERN LEADS THIS SCREEN. Dr. Rami's ruling on mockup B:
//   "when starting a case 'General Concern' needs to be an option". Home no longer carries the
//   General card, so this is now the ONLY door into the General questionnaire. That makes its
//   silent failure the one worth a gate: if 'general' is renamed or flipped to
//   patientSelectable:false, the card simply does not render and nothing says so (§1dk(e)).
//   check-specialty-resolver ARM GC-1 reads this constant and the shipped table.
const GENERAL_AREA_ID = 'general'

function TypingDots() {
  return (
    <div className="flex gap-1.5 py-1.5">
      {[0, 0.15, 0.3].map((delay, i) => (
        <span
          key={i}
          className="w-[6px] h-[6px] rounded-full"
          style={{ backgroundColor: '#A7D9FC', animation: `browseDotPulse 1.2s ease-in-out infinite ${delay}s` }}
        />
      ))}
    </div>
  )
}

// Fold accents and case so "Veins" finds "veins" and "ear" finds "Ear".
const fold = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

// ★★ s117 (D529, canvas "MD Panel Add Case Drawer" N1 + P1, approved 2026-10-23). ONE header bar (back, context,
//   bell), then the Orella dialogue, the search and the rows. The "What's your concern?" title is gone (the dialogue
//   asks), and General concern is no longer a lead card: it is "General complaint", a row of the same weight sorted
//   in among the areas. With `state.mdCaseId` (a case the physician started with no area, utils/mdCaseArea) the
//   header is the physician and the list is his areas plus General complaint.
export default function SpecialtyBrowseScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const mdCaseId = location.state?.mdCaseId || null
  const { cases } = useCaseStore()
  const mdCase = mdCaseId ? (cases || []).find((c) => c.id === mdCaseId) : null
  const mdDoctor = mdCase ? caseDoctor(mdCase) : null
  const mdSpecialties = mdDoctor ? (mdDoctor.specialties?.length ? mdDoctor.specialties : (mdDoctor.specialty ? [mdDoctor.specialty] : [])) : []
  const mdName = mdDoctor ? clinicianName(mdDoctor, 'short') : ''
  const { t, language } = useI18n()
  const { tfa } = useQuestionnaireTranslation()
  const { specialties } = useQuestionnaireStore()

  const [query, setQuery] = useState('')
  const [learnArea, setLearnArea] = useState(null)
  const [areaDetails, setAreaDetails] = useState({})
  const [areaLoading, setAreaLoading] = useState(false)

  const allAreas = useMemo(() => browsableSpecialties(specialties), [specialties])
  const areas = useMemo(
    () => (mdCaseId ? doctorAreas(allAreas, mdSpecialties, doctorMatchesCategory) : allAreas),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allAreas, mdCaseId, mdSpecialties.join('|')]
  )
  const general = useMemo(
    () => (specialties || []).find((s) => s.id === GENERAL_AREA_ID && isPatientSelectable(s)) || null,
    [specialties]
  )

  // v229.0.0: t() echoes the key when a translation is missing, and a key is
  // truthy — so the unresolved case has to be detected explicitly, never with `||`.
  const displayName = (s) => {
    if (s.id === GENERAL_AREA_ID) return t('home.generalComplaint', 'General complaint')
    const k = `specialties.${s.id}`
    const tr = t(k)
    return tr && tr !== k ? tr : s.name
  }

  const results = useMemo(() => {
    const q = fold(query)
    const rows = withGeneral(areas, general, displayName)
    if (!q) return rows
    return rows.filter((s) => {
      if (fold(displayName(s)).includes(q)) return true
      if (fold(s.name).includes(q)) return true
      if (fold(s.id).replace(/-/g, ' ').includes(q)) return true
      return (s.focusAreas || []).some((f) => fold(tfa(f.name)).includes(q) || fold(f.name).includes(q))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas, general, query, language])

  const openLearnMore = async (specialty) => {
    setLearnArea(specialty)
    if (areaDetails[specialty.id]) return
    setAreaLoading(true)
    const fallback = t('home.learnMoreUnavailable', "I couldn't load a description right now — but you can still open this area to get started.")
    try {
      const reviewedBy = (specialty.matchingDoctorSpecialties || []).join(', ')
      const resp = await aiAPI.getAreaDetail(displayName(specialty), reviewedBy, language)
      setAreaDetails((m) => ({ ...m, [specialty.id]: resp?.detail || fallback }))
    } catch {
      setAreaDetails((m) => ({ ...m, [specialty.id]: fallback }))
    } finally {
      setAreaLoading(false)
    }
  }

  return (
    <div className="min-h-full bg-white flex flex-col">
      <style>{`
        @keyframes browseDotPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.1); }
        }
      `}</style>

      <div className="sticky-page-header px-2 h-[60px] flex items-center gap-2.5" style={{ borderBottom: '1px solid #E7EEF4' }}>
        <button onClick={() => navigate(mdCaseId ? '/cases' : '/home')} className="w-11 h-11 flex items-center justify-center" style={{ color: MUTE }} aria-label={t('common.back', 'Back')}>
          <ArrowLeft size={22} />
        </button>
        {mdDoctor ? (
          <>
            <span className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center text-[14px] font-extrabold flex-shrink-0" style={{ background: ICE, color: NAVY }}>
              {`${(mdDoctor.first_name || '')[0] || ''}${(mdDoctor.last_name || '')[0] || ''}`.toUpperCase() || 'DR'}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[16px] font-extrabold leading-tight truncate" style={{ color: NAVY }}>{clinicianName(mdDoctor)}</span>
              <span className="block text-[13px] font-medium truncate" style={{ color: MUTE }}>{mdSpecialties[0] || ''}</span>
            </span>
          </>
        ) : (
          <h1 className="flex-1 text-[17px] font-extrabold" style={{ color: NAVY }}>{t('home.newCase', 'New case')}</h1>
        )}
        <NotificationBellButton variant="compact" />
      </div>

      <div className="px-4 pt-4">
        <OrellaDialogue>
          {mdDoctor
            ? t('home.mdCaseDialogue', { defaultValue: '{{doctor}} started a case for you. Pick an area and we can dive into the details.', doctor: mdName })
            : t('home.newCaseDialogue', "Let's get started. Pick an area and we can dive into the details.")}
        </OrellaDialogue>
      </div>

      <div className="px-4 pt-2 pb-3">
        <div
          className="flex items-center gap-2.5 rounded-2xl px-3.5 py-2.5 bg-white"
          style={{ border: `1.25px solid ${LINE}` }}
        >
          <Search size={17} style={{ color: '#94A3B8', flexShrink: 0 }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('home.searchAreasPlaceholder', 'Search areas or symptoms')}
            className="flex-1 min-w-0 bg-transparent outline-none text-[14px] font-medium"
            style={{ color: NAVY }}
          />
          {query && (
            <button onClick={() => setQuery('')} className="p-0.5" style={{ color: '#94A3B8' }}>
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="px-4 pb-10 flex-1">
        {results.length === 0 ? (
          <div className="pt-10 text-center">
            <p className="text-[14.5px] font-bold" style={{ color: NAVY }}>
              {t('home.noAreasMatch', 'No areas match that search')}
            </p>
            <p className="text-[12.5px] font-medium mt-1.5" style={{ color: MUTE }}>
              {t('home.tryAnotherWord', 'Try a different word, or the part of the body it affects.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-4">
            {results.map((specialty) => {
              const iconSrc = getSpecialtyIcon(specialty)
              const name = displayName(specialty)
              return (
                <div
                  key={specialty.id}
                  className="flex items-center gap-3 py-2.5 min-w-0"
                  style={{ borderBottom: '1px solid #EEF3F7' }}
                >
                  <button
                    onClick={() => navigate(`/specialty/${specialty.id}`, mdCaseId ? { state: { mdCaseId } } : undefined)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left active:opacity-70 transition-opacity"
                  >
                    <span
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: ICE, border: '1px solid #DCEFFB' }}
                    >
                      {iconSrc ? (
                        <img src={iconSrc} alt="" className="w-[30px] h-[30px] object-contain" />
                      ) : (
                        <Stethoscope className="w-5 h-5" style={{ color: BLUE }} />
                      )}
                    </span>
                    <span className="flex-1 min-w-0 text-[15px] font-bold truncate" style={{ color: NAVY }}>
                      {name}
                    </span>
                  </button>
                  <button
                    onClick={() => openLearnMore(specialty)}
                    aria-label={`${t('home.learnMore', 'Learn more')}: ${name}`}
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 active:scale-[0.94] transition-transform"
                    style={{ background: ICE, border: '1px solid #DCEFFB', color: BLUE }}
                  >
                    <Sparkles size={14} />
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <BottomSheet open={!!learnArea} onClose={() => setLearnArea(null)}>
        {learnArea && (() => {
          const name = displayName(learnArea)
          const icon = getSpecialtyIcon(learnArea)
          const detail = areaDetails[learnArea.id]
          const covers = learnArea.focusAreas || []
          const reviewedBy = (learnArea.matchingDoctorSpecialties || []).join(' · ')
          return (
            <div className="px-5 pb-6 pt-1 overflow-y-auto">
              <div className="flex items-center gap-3 mb-3.5">
                <div className="w-12 h-12 rounded-2xl bg-[#E9F8FF] border border-[#DCEFFB] flex items-center justify-center flex-shrink-0">
                  {icon ? (
                    <img src={icon} alt="" className="w-9 h-9 object-contain" />
                  ) : (
                    <Stethoscope className="w-6 h-6 text-[#46A8F9]" />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="text-[18px] font-extrabold text-[#15354F] leading-tight">{name}</h3>
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#46A8F9] mt-0.5">
                    <Sparkles size={12} /> {t('home.generatedForYou', 'Generated for you')}
                  </span>
                </div>
              </div>
              <div className="min-h-[60px]">
                {areaLoading && !detail ? (
                  <TypingDots />
                ) : (
                  <p className="text-[13.5px] leading-relaxed text-[#2b4a63] font-medium">{detail}</p>
                )}
              </div>
              {covers.length > 1 && (
                <div className="mt-4">
                  <p className="text-[11px] font-bold text-[#5F7A8E] uppercase tracking-[0.06em] mb-2">
                    {t('home.whatThisCovers', 'What this covers')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {covers.map((f) => (
                      <span
                        key={f.id}
                        className="text-[12px] font-semibold text-[#15354F] bg-[#E9F8FF] border border-[#DCEFFB] rounded-full px-2.5 py-1"
                      >
                        {tfa(f.name)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {reviewedBy && (
                <p className="text-[12.5px] text-[#5F7A8E] mt-4">
                  {t('home.typicallyReviewedBy', 'Typically reviewed by')}{' '}
                  <span className="font-bold text-[#15354F]">{reviewedBy}</span>
                </p>
              )}
              <div className="flex gap-2.5 mt-5">
                <button
                  onClick={() => { const id = learnArea.id; setLearnArea(null); navigate(`/specialty/${id}`, mdCaseId ? { state: { mdCaseId } } : undefined) }}
                  className="flex-1 rounded-2xl py-3.5 text-[14.5px] font-bold text-[#0B3D17] bg-[#66D170] active:scale-[0.98] transition-transform"
                >
                  {t('home.startACase', 'Start a case')}
                </button>
                <button
                  onClick={() => setLearnArea(null)}
                  className="px-5 rounded-2xl py-3.5 text-[14.5px] font-bold text-[#15354F] bg-white border border-[#E4EBF2] active:scale-[0.98] transition-transform"
                >
                  {t('common.close', 'Close')}
                </button>
              </div>
            </div>
          )
        })()}
      </BottomSheet>
    </div>
  )
}
